import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import BloodPressureInput from '../ui/blood-pressure-input';
import HeightInput from '../ui/height-input';
import WeightInput from '../ui/weight-input';
import { PatientFormData } from './patientForm.types';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FileText, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ClinicalInformationSectionProps {
  form: UseFormReturn<PatientFormData>;
  notesRows: number;
  setNotesRows: React.Dispatch<React.SetStateAction<number>>;
  isGuided?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ClinicalInformationSection = ({
  form,
  notesRows,
  setNotesRows,
  isGuided = false,
  isOpen = true,
  onOpenChange,
}: ClinicalInformationSectionProps) => {
  const hasErrors = Boolean(form.formState.errors.note);

  const content = (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <WeightInput
            form={form}
            name='weight'
            label='Weight'
            placeholder='Enter weight'
            required={false}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='weightRecordedDate'>Weight Recorded Date</Label>
          <DatePicker
            value={
              form.watch('weight_recorded_date')
                ? (() => {
                    const dateStr = form.watch('weight_recorded_date');
                    // Handle both 'yyyy-MM-dd' and 'MM/DD/YYYY' formats
                    if (dateStr.includes('/')) {
                      const [month, day, year] = dateStr.split('/');
                      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    } else if (dateStr.includes('-')) {
                      const [year, month, day] = dateStr.split('-');
                      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    }
                    return new Date(dateStr);
                  })()
                : undefined
            }
            onValueChange={date => {
              const dateString = date ? format(date, 'yyyy-MM-dd') : '';
              form.setValue('weight_recorded_date', dateString, {
                shouldValidate: true,
              });
            }}
            placeholder='Select weight recorded date'
            maxDate={new Date()}
          />
        </div>
      </div>
      <div className='space-y-1.5'>
        <HeightInput
          form={form}
          name='height'
          label='Height'
          placeholder='Enter height'
          required={false}
        />
      </div>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <BloodPressureInput
            form={form}
            systolicName='blood_pressure_systolic'
            diastolicName='blood_pressure_diastolic'
            label='Blood Pressure'
            required={false}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='bpRecordedDate'>BP Recorded Date</Label>
          <DatePicker
            value={
              form.watch('blood_pressure_recorded_at')
                ? (() => {
                    const dateStr = form.watch('blood_pressure_recorded_at');
                    if (dateStr.includes('/')) {
                      const [month, day, year] = dateStr.split('/');
                      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    } else if (dateStr.includes('-')) {
                      const [year, month, day] = dateStr.split('-');
                      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    }
                    return new Date(dateStr);
                  })()
                : undefined
            }
            onValueChange={date => {
              const dateString = date ? format(date, 'yyyy-MM-dd') : '';
              form.setValue('blood_pressure_recorded_at', dateString, {
                shouldValidate: true,
              });
            }}
            placeholder='Select BP recorded date'
            maxDate={new Date()}
          />
        </div>
      </div>
      <div className='space-y-1.5'>
        <Label htmlFor='patientNote'>Patient Note</Label>
        <Textarea
          id='patientNote'
          name='note'
          rows={notesRows}
          {...form.register('note')}
          onChange={e => {
            const value = e.target.value || '';
            form.setValue('note', value.trimStart(), { shouldValidate: true });
            setNotesRows(
              value.split('\n').length > 8
                ? value.split('\n').length > 20
                  ? 20
                  : value.split('\n').length
                : 8
            );
          }}
          placeholder='Additional patient notes...'
        />
        {form.formState.errors.note && (
          <p className='text-[0.8rem] text-red-500'>{form.formState.errors.note.message}</p>
        )}
      </div>
    </div>
  );

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <button className="flex w-full items-center justify-between py-3 text-sm font-semibold text-slate-700 hover:text-slate-900">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Clinical Information
            {hasErrors && <span className="h-2 w-2 rounded-full bg-red-500" />}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-normal">Optional</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className='space-y-4 pt-2'>
          {content}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ClinicalInformationSection;
